/**
 * Overlay Library
 *
 * Action bar button with an anchored dropdown for toggling chart overlays.
 *
 * Was a centred modal with a full-screen backdrop, which made picking an
 * overlay feel like a settings trip rather than a toggle: it took over the
 * screen, hid the chart you were deciding against, and had to be dismissed
 * deliberately. Every other action-bar control — Levels, Signals, S/R — is a
 * dropdown anchored under its own button, so this is now the same thing.
 *
 * The pattern is LevelsToggle's, deliberately down to the details: same
 * `action-bar-btn` trigger, same fixed-position portal so the panel escapes the
 * action bar's clipping, same `rect.bottom + 4` / `rect.left - 120` anchoring,
 * same mousedown-outside dismissal, same z-index. Copying it exactly is the
 * point — three near-identical dropdowns that each behave slightly differently
 * is worse than three that behave the same.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  useOverlayLibraryStore,
  OVERLAY_DEFINITIONS,
  OverlayId,
} from '../store/overlayLibraryStore';
import { usePreferences } from '../hooks/usePreferences';
import { Layers, Check, X } from 'lucide-react';
import './OverlayLibrary.css';

const OverlayLibrary: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  const { visibleOverlays, toggleOverlay, syncFromPreferences } = useOverlayLibraryStore();
  const { preferences, isLoading } = usePreferences();
  const hasSynced = useRef(false);

  // Sync with preferences on mount (one-time)
  useEffect(() => {
    if (!isLoading && preferences.overlayLibrary && !hasSynced.current) {
      hasSynced.current = true;
      syncFromPreferences();
    }
  }, [isLoading, preferences.overlayLibrary, syncFromPreferences]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  // Anchor the dropdown under the button. Measured on open rather than kept in
  // sync, matching LevelsToggle: the action bar does not move while a dropdown
  // is up, and a resize listener would be machinery for a case that cannot
  // happen.
  useEffect(() => {
    if (isExpanded && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.left - 120),
      });
    }
  }, [isExpanded]);

  // Close on click outside. `mousedown` rather than `click` so the dropdown is
  // gone before whatever was clicked reacts.
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        expandedRef.current &&
        !expandedRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, handleClose]);

  const handleToggle = (id: OverlayId) => {
    toggleOverlay(id);
  };

  const enabledCount = visibleOverlays.length;

  return (
    <div ref={containerRef} className="overlay-library">
      <button
        className={`overlay-library-button action-bar-btn ${enabledCount > 0 ? 'overlay-library-enabled' : ''}`}
        onClick={handleToggleExpand}
        title="Library"
      >
        <Layers size={14} className="action-bar-icon" />
        <span className="action-bar-label">Library</span>
        {enabledCount > 0 && enabledCount < OVERLAY_DEFINITIONS.length && (
          <span className="overlay-library-count">{enabledCount}</span>
        )}
      </button>

      {/* Portalled so the panel is not clipped by the action bar's overflow,
          and fixed-positioned because the anchor was measured in viewport
          coordinates. */}
      {isExpanded &&
        createPortal(
          <div
            ref={expandedRef}
            className="overlay-library-dropdown"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 10000,
            }}
          >
            <div className="overlay-library-dropdown-header">
              <span>Library</span>
              <button
                className="overlay-library-close"
                onClick={handleClose}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>

            <div className="overlay-library-content">
              <div className="overlay-library-list">
                {OVERLAY_DEFINITIONS.map((overlay) => (
                  <button
                    key={overlay.id}
                    className={`overlay-library-item ${visibleOverlays.includes(overlay.id) ? 'active' : ''}`}
                    onClick={() => handleToggle(overlay.id)}
                  >
                    <div className="overlay-info">
                      <div className="overlay-name">{overlay.label}</div>
                      <div className="overlay-description">{overlay.description}</div>
                    </div>
                    <div className="overlay-toggle">
                      {visibleOverlays.includes(overlay.id) && <Check size={16} />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OverlayLibrary;
